import { Navigate, useLocation } from 'react-router-dom';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { getAvailableActions, getResourceActions, getWriteActions } from '../../utils/permissionCatalog';
import { checkPermissionActions } from '../../utils/permissions';
import { readMetrcSsoRedirectState } from '../../utils/metrcSsoRedirectState';
import { useImportsWizard, type MetrcImportWorkflow } from './ImportsWizardContext';
import type { ReactNode } from 'react';

export default function MetrcImportRouteGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { currentUser } = useLegacyAppSession();
  const { workflow } = useImportsWizard();
  // The SSO callback lands after a full-page redirect, which remounts the app and
  // resets the in-memory wizard context - read the stashed workflow directly instead.
  const routeWorkflow = location.pathname.endsWith('/auth')
    ? location.state?.workflow
    : location.pathname.endsWith('/sso-callback')
      ? readMetrcSsoRedirectState()?.workflow
      : workflow;
  const actions = getAvailableActions(currentUser);
  const canImport = checkPermissionActions(actions, getWriteActions('Imports'));
  // Signing in again only saves the user's own tokens, which the server allows to anyone who may import.
  if (routeWorkflow === 'reauth' && location.pathname.endsWith('/sso-callback')) {
    return canImport ? <>{children}</> : <Navigate to="/profile" replace />;
  }
  const selectedWorkflow: MetrcImportWorkflow = routeWorkflow === 'facilities' || routeWorkflow === 'products'
    ? routeWorkflow
    : 'integration';
  const canImportFacilities = selectedWorkflow !== 'products'
    ? checkPermissionActions(actions, getWriteActions('Facilities'))
    : true;
  const canImportProducts = selectedWorkflow !== 'facilities'
    ? checkPermissionActions(actions, getWriteActions('Products'))
    : true;
  const canReadFacilitiesForProductImport = selectedWorkflow === 'products'
    ? checkPermissionActions(actions, getResourceActions('Facilities'))
    : true;

  return canImport && canImportFacilities && canImportProducts && canReadFacilitiesForProductImport
    ? <>{children}</>
    : <Navigate to="/profile" replace />;
}
